import { addRatingComment } from "@/modules/lead-page/api/lead-page.v2.api";
import { AddRatingCommentDto } from "@/modules/lead-page/dtos/index-v2";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosResponse } from "axios";
import { toast } from "react-toastify";
import { useCallback } from "react";

interface AddRatingCommentParams {
  ratingUId: string;
  data: AddRatingCommentDto;
}

interface UseAddRatingCommentOptions {
  ratingUId: string;
  onSuccess?: () => void;
}

export const useAddRatingComment = ({
  ratingUId,
  onSuccess,
}: UseAddRatingCommentOptions) => {
  const queryClient = useQueryClient();

  const mutation = useMutation<
    AxiosResponse<any>,
    { response?: { data?: { message?: string } } },
    AddRatingCommentParams
  >(({ ratingUId, data }) => addRatingComment(ratingUId, data), {
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["leadRatings"] });
      queryClient.invalidateQueries({
        queryKey: ["leadRatingDetails", variables.ratingUId],
      });
      toast.success("Comment added successfully", {
        position: "bottom-left",
        autoClose: 3000,
      });
      onSuccess?.();
    },
    onError: (error) => {
      const errorMessage =
        error?.response?.data?.message || "Failed to add comment";
      toast.error(errorMessage, {
        position: "bottom-left",
        autoClose: 3000,
      });
    },
  });

  const handleSendComment = useCallback(
    (comment: string) => {
      const trimmedComment = comment.trim();
      if (!trimmedComment || mutation.isPending) return;

      mutation.mutate({
        ratingUId,
        data: { comment: trimmedComment },
      });
    },
    [ratingUId, mutation]
  );

  return {
    handleSendComment,
    isPending: mutation.isPending,
  };
};
